// features/common/SignatureModal.js
// Allows for users to add signature data with canvas finger drawing.
// Also allows for an array of components to be added to the modal through a list. All components can be added, but need to hold their own functions and data

// Signature modal that can be used for employee signatures.
import React, { useRef, useEffect, useState } from "react";
import styles from "./SignatureModal.module.css";

export const SignatureModal = ({
  isOpen,
  onClose,
  signee,
  onSave,
  onClear,
  existingSignature,
  components = null,
}) => {
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });
  const [hasSignature, setHasSignature] = useState(false);

  // Track points for signature validation
  const points = useRef([]);
  const requestRef = useRef();
  const lastDrawTime = useRef(0);

  useEffect(() => {
    if (!isOpen) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // Set canvas dimensions to match its display size
    const setCanvasDimensions = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;

      // Set drawing styles
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#000";
    };

    // Initialize canvas dimensions
    setCanvasDimensions();

    // Draw existing signature if available
    if (existingSignature) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        setHasSignature(true);
        points.current = [
          { x: 10, y: 10 },
          { x: canvas.width - 10, y: canvas.height - 10 },
        ]; // Add points to pass validation
      };
      img.src = existingSignature;
    }

    // Get position within the canvas
    const getPos = (e) => {
      const rect = canvas.getBoundingClientRect();
      const clientX =
        e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
      const clientY =
        e.clientY || (e.touches && e.touches[0] ? e.touches[0].clientY : 0);

      return {
        x: clientX - rect.left,
        y: clientY - rect.top,
      };
    };

    const getPressure = (e) => {
      return (
        e.pressure ||
        e.force ||
        (e.touches && e.touches[0] ? e.touches[0].force : 0.5) ||
        0.5
      );
    };

    // Throttled drawing function
    const draw = (e) => {
      if (!isDrawing.current) return;

      const now = Date.now();
      if (now - lastDrawTime.current < 16) return; // ~60fps
      lastDrawTime.current = now;

      const pos = getPos(e);
      const pressure = getPressure(e);

      ctx.beginPath();
      ctx.moveTo(lastPos.current.x, lastPos.current.y);
      ctx.lineTo(pos.x, pos.y);

      // Adjust line width based on pressure
      ctx.lineWidth = Math.max(1, pressure * 4);

      ctx.stroke();
      points.current.push(pos);
      lastPos.current = pos;
      setHasSignature(true);
    };

    const handleStart = (e) => {
      e.preventDefault();
      isDrawing.current = true;
      lastPos.current = getPos(e);
      points.current.push(lastPos.current);
    };

    const handleMove = (e) => {
      e.preventDefault();
      if (!isDrawing.current) return;
      requestRef.current = requestAnimationFrame(() => draw(e));
    };

    const handleEnd = (e) => {
      e.preventDefault();
      isDrawing.current = false;
      cancelAnimationFrame(requestRef.current);
    };

    // Add both mouse and touch event listeners
    const addListeners = (el) => {
      // Touch events
      el.addEventListener("touchstart", handleStart, { passive: false });
      el.addEventListener("touchmove", handleMove, { passive: false });
      el.addEventListener("touchend", handleEnd);
      el.addEventListener("touchcancel", handleEnd);

      // Mouse events
      el.addEventListener("mousedown", handleStart);
      el.addEventListener("mousemove", handleMove);
      el.addEventListener("mouseup", handleEnd);
      el.addEventListener("mouseout", handleEnd);
    };

    const removeListeners = (el) => {
      el.removeEventListener("touchstart", handleStart);
      el.removeEventListener("touchmove", handleMove);
      el.removeEventListener("touchend", handleEnd);
      el.removeEventListener("touchcancel", handleEnd);

      el.removeEventListener("mousedown", handleStart);
      el.removeEventListener("mousemove", handleMove);
      el.removeEventListener("mouseup", handleEnd);
      el.removeEventListener("mouseout", handleEnd);
    };

    // Handle window resize
    const handleResize = () => {
      setCanvasDimensions();
    };

    window.addEventListener("resize", handleResize);
    addListeners(canvas);

    return () => {
      window.removeEventListener("resize", handleResize);
      removeListeners(canvas);
    };
  }, [isOpen, existingSignature]);

  // Removed the size validation - we still need some points for validation
  const validateSignature = () => {
    return points.current.length > 0;
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    points.current = [];
    setHasSignature(false);
  };

  const handleSave = () => {
    if (!hasSignature) {
      const canvas = canvasRef.current;
      // Save null signature to clear
      onSave(null, signee);
      onClose();
      return;
    }

    if (!validateSignature()) {
      alert("Please provide a signature");
      return;
    }

    const canvas = canvasRef.current;
    // Compress signature data
    const signatureData = canvas.toDataURL("image/png", 0.5);
    // Save Signature function passed in as parameter
    onSave(signatureData, signee);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        {components && (
          <div className={styles.componentsContainer}>{components}</div>
        )}
        <p>Please sign below:</p>
        <div className={styles.signaturePadContainer}>
          <canvas ref={canvasRef} className={styles.signatureCanvas} />
        </div>
        <div className={styles.modalButtons}>
          <button onClick={handleSave} className={styles.saveButton}>
            Save
          </button>
          <button onClick={handleClear} disabled={!hasSignature}>
            Clear Signature
          </button>
          <button onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};
